import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018-2021 semantics GmbH
// Date        : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf, Andreas Hesse
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 159808 $
// Purpose     : dialogs
// =============================================================================
// ---Third-party imports
import React, { useState } from 'react';
import { Button, Modal, Header, Transition } from 'semantic-ui-react';
import { Rnd } from 'uix/chunks';
import KeyHandler, { KEYDOWN } from 'react-key-handler';
import clsx from 'clsx';
import { isElement } from 'react-is';
// ---Framework imports
import { isNumber, callIfFunc, isNil } from 'ldash';
import { isSSR } from 'uix/utils';
import { usePersistentShallow, useFullscreen, useMemo, useFullscreenContext, FullscreenContext } from 'uix/hooks';
import log from 'uix/logging';
import { VIcon } from 'uix/graphics';
// ---Local imports
import { AutoPortal } from './portals';
import styles from './dialog.scss';
// ---Exports
const ButtonCancel = (props)=>/*#__PURE__*/ _jsx(Button, {
        inverted: true,
        color: "grey",
        content: "Abbrechen",
        ...props
    });
const ButtonOk = (props)=>/*#__PURE__*/ _jsx(Button, {
        content: "Ok",
        ...props
    });
export const Dialog = ({ size = 'large', open, icon, header, subheader, content, onClose, onCancel, onSubmit, submitDisabled = false, children, buttons, style, trigger })=>{
    let { domNode } = useFullscreenContext();
    let [triggerOpen, setTriggerOpen] = useState(false);
    let triggerEl = null;
    if (isElement(trigger)) {
        // assume single element like button
        triggerEl = /*#__PURE__*/ React.cloneElement(trigger, {
            onClick: ()=>setTriggerOpen(!triggerOpen)
        });
    } else if (trigger) {
        log.warn('bad trigger, should be a single react element');
    }
    let onSubmitClick = onSubmit;
    let onCancelClick = onCancel;
    if (trigger) {
        if (isNil(open)) {
            open = triggerOpen;
        }
        onClose = ()=>{
            setTriggerOpen(false);
        };
        onSubmitClick = ()=>{
            setTriggerOpen(false);
            callIfFunc(onSubmit);
        };
        onCancelClick = ()=>{
            setTriggerOpen(false);
            callIfFunc(onCancel);
        };
    }
    let actions = buttons || onCancel || onSubmit;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            triggerEl,
            /*#__PURE__*/ _jsxs(Modal, {
                style: style,
                size: size,
                open: open,
                onClose: onClose,
                mountNode: domNode || undefined,
                children: [
                    (header || icon) && /*#__PURE__*/ _jsxs(Header, {
                        children: [
                            icon && /*#__PURE__*/ _jsx(VIcon, {
                                name: icon
                            }),
                            /*#__PURE__*/ _jsxs(Header.Content, {
                                children: [
                                    header,
                                    subheader && /*#__PURE__*/ _jsx(Header.Subheader, {
                                        children: subheader
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Modal.Content, {
                        scrolling: true,
                        children: children || content
                    }),
                    actions && /*#__PURE__*/ _jsx(Modal.Actions, {
                        children: buttons ? buttons : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                onCancel && /*#__PURE__*/ _jsx(ButtonCancel, {
                                    onClick: onCancelClick
                                }),
                                onSubmit && /*#__PURE__*/ _jsx(ButtonOk, {
                                    onClick: onSubmitClick,
                                    disabled: submitDisabled
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
};
Dialog.ButtonCancel = ButtonCancel;
Dialog.ButtonOk = ButtonOk;
export const TransitionDialog = ({ open, style, position = 'top', children, animation = 'fade', duration = 300 })=>{
    const styles = {
        [position]: '-2px',
        ...style
    };
    const animations = {
        top: `${animation} down`,
        bottom: `${animation} up`,
        left: `${animation} right`,
        right: `${animation} left`
    }[position];
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Transition.Group, {
                animation: animations,
                duration: duration,
                children: open && /*#__PURE__*/ _jsx(Modal, {
                    style: styles,
                    open: open,
                    size: "mini",
                    children: children
                })
            }),
            ' '
        ]
    });
};
const INITIAL_OPTIONS = {
    size: {
        width: null,
        height: null
    },
    position: {
        x: null,
        y: null
    }
};
export const ResizableDialog = ({ name = 'unnamedDlg', storeKey, children, className, header, icon, onClose, closeOnEsc = false, closeButton = true, fullscreenButton = true, overflow = 'auto', flexInner = false, minWidth = 240, minHeight = 120, onSubmit = false, initialPosition = null, initialSize = {
    width: 640,
    height: 400
}, trigger })=>{
    /* a dialog with dragging & resizing support
   */ let [triggerOpen, setTriggerOpen] = useState(false);
    let { isFullscreen: inFullscreenContext, ref: inFullscreenRef } = useFullscreenContext();
    let portalTarget = inFullscreenContext ? 'fullscreenRootPortal' : 'rootPortal';
    let { ref, toggleFullscreen, isFullscreen } = useFullscreen();
    let fullscreenContextValue = {
        isFullscreen,
        ref
    };
    if (!isFullscreen && inFullscreenContext) {
        // pass through context info if we are in outer fullscreen
        fullscreenContextValue = {
            isFullscreen: true,
            ref: inFullscreenRef
        };
    }
    let [opts, setOpts] = usePersistentShallow(`${storeKey || name}`, INITIAL_OPTIONS, 3);
    let { position, size } = useMemo(()=>{
        let size = {
            width: opts.size.width,
            height: opts.size.height
        };
        if (!isNumber(size.width)) {
            size.width = initialSize.width;
        }
        if (!isNumber(size.height)) {
            size.height = initialSize.height;
        }
        let { x, y } = opts.position;
        if (!initialPosition) {
            let [iw, ih] = isSSR ? [
                1600,
                900
            ] : [
                window.innerWidth,
                window.innerHeight
            ];
            if (!isNumber(x)) {
                // centered
                x = iw / 2 - size.width / 2;
            }
            if (!isNumber(y)) {
                // 30% from top
                y = ih / 3 - size.height / 2;
            }
            x = Math.max(Math.min(x, iw - size.width), 0);
            y = Math.max(Math.min(y, ih - size.height), 0);
        } else {
            if (!isNumber(x)) {
                x = initialPosition.x;
            }
            if (!isNumber(y)) {
                y = initialPosition.y;
            }
        }
        return {
            position: {
                x,
                y
            },
            size
        };
    }, [
        opts.position,
        opts.size
    ]);
    let handleClass = `dialog_handle_${name}`;
    let innerStyle = {
        overflow
    };
    if (flexInner) {
        // flexDirection=row works nicely with react-table (see StoreCheckResult)
        innerStyle = {
            ...innerStyle,
            display: 'flex',
            flex: 1,
            flexDirection: 'row'
        };
    }
    let close = ()=>{
        setTriggerOpen(false);
        callIfFunc(onClose);
    };
    // we do want do want to propagate doubleClicks further upward
    // i'v seen bad fullscreen switches on nested dialogs
    let catchDoubleClicks = (event)=>event.stopPropagation();
    let dialog = /*#__PURE__*/ _jsx(AutoPortal, {
        target: portalTarget,
        children: /*#__PURE__*/ _jsxs(Rnd, {
            bounds: "window",
            className: clsx(className, styles.dialog),
            size: size,
            style: {
                display: 'flex'
            },
            position: position,
            onDragStop: (e, d)=>{
                setOpts({
                    position: {
                        x: d.x,
                        y: d.y
                    }
                });
            },
            onResizeStop: (e, direction, ref, delta, position)=>{
                setOpts({
                    size: {
                        width: ref.offsetWidth,
                        height: ref.offsetHeight
                    },
                    position
                });
            },
            dragHandleClassName: handleClass,
            minWidth: minWidth,
            minHeight: minHeight,
            onDoubleClick: catchDoubleClicks,
            children: [
                closeOnEsc && /*#__PURE__*/ _jsx(KeyHandler, {
                    keyEventName: KEYDOWN,
                    code: 'Escape',
                    onKeyHandle: close
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: clsx(handleClass, styles.header),
                    onDoubleClick: ()=>fullscreenButton ? callIfFunc(toggleFullscreen) : null,
                    children: [
                        /*#__PURE__*/ _jsxs("h3", {
                            className: styles.headerText,
                            children: [
                                icon && /*#__PURE__*/ _jsx(VIcon, {
                                    className: styles.headerIcon,
                                    name: icon
                                }),
                                header
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.buttons,
                            children: [
                                fullscreenButton && /*#__PURE__*/ _jsx(Button, {
                                    icon: true,
                                    onClick: toggleFullscreen,
                                    className: clsx(styles.headerButton, styles.maximize),
                                    secondary: true,
                                    children: /*#__PURE__*/ _jsx(VIcon, {
                                        name: "square"
                                    })
                                }),
                                closeButton && /*#__PURE__*/ _jsx(Button, {
                                    icon: true,
                                    secondary: true,
                                    onClick: (event)=>{
                                        event.stopPropagation();
                                        close();
                                    },
                                    className: clsx(styles.headerButton, styles.close),
                                    children: /*#__PURE__*/ _jsx(VIcon, {
                                        name: "close"
                                    })
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    ref: ref,
                    className: clsx(styles.content, isFullscreen && styles.fullscreen),
                    style: innerStyle,
                    children: /*#__PURE__*/ _jsx(FullscreenContext.Provider, {
                        value: fullscreenContextValue,
                        children: children
                    })
                }),
                onSubmit && /*#__PURE__*/ _jsx("div", {
                    className: styles.actions,
                    children: /*#__PURE__*/ _jsx(Button, {
                        color: "grey",
                        style: {
                            float: 'right'
                        },
                        onClick: onSubmit,
                        children: "OK"
                    })
                })
            ]
        })
    });
    if (trigger) {
        trigger = isElement(trigger) ? /*#__PURE__*/ React.cloneElement(trigger, {
            onClick: ()=>setTriggerOpen(true)
        }) : 'bad trigger';
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                trigger,
                triggerOpen && dialog
            ]
        });
    }
    return dialog;
};
